import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { RootState } from '.';
import { FunnelStatus } from '../types/enums/FunnelStatus';
import type { Dictionary } from 'lodash';

export interface FunnelStatusState {
  funnelStatusMap: Dictionary<FunnelStatus>;
}

const initialState: FunnelStatusState = {
  funnelStatusMap: {},
};
type StrippedFunnel = Pick<Funnel, 'id' | 'pendingChanges' | 'isDeployed' | 'crmOrgId'>;

const getFunnelStatusFromStrippedFunnel = (funnel: StrippedFunnel) => {
  if (funnel.isDeployed) {
    return funnel.pendingChanges ? FunnelStatus.PENDING_CHANGES : FunnelStatus.DEPLOYED;
  }
  if (funnel.isDeployed === undefined) {
    return FunnelStatus.UNREACHABLE;
  }
  return FunnelStatus.DRAFT;
};

export const funnelStatusSlice = createSlice({
  name: 'funnelStatus',
  initialState,
  reducers: {
    setFunnelStatusFromFunnel: (state, action: PayloadAction<StrippedFunnel>) => {
      const status = getFunnelStatusFromStrippedFunnel(action.payload);
      state.funnelStatusMap[action.payload.id] = status;
    },
    setMultipleFunnelStatuses: (state, action: PayloadAction<StrippedFunnel[]>) => {
      action.payload.forEach((funnel) => {
        state.funnelStatusMap[funnel.id] = getFunnelStatusFromStrippedFunnel(funnel);
      });
    },
    removeFunnelStatus: (state, action: PayloadAction<string>) => {
      const funnelId = action.payload;
      delete state.funnelStatusMap[funnelId];
    },
    clearAllFunnelStatuses: (state) => {
      state.funnelStatusMap = {};
    },
  },
});

export const {
  setFunnelStatusFromFunnel,
  setMultipleFunnelStatuses,
  removeFunnelStatus,
  clearAllFunnelStatuses,
} = funnelStatusSlice.actions;

// Selectors
export const selectFunnelStatusMap = (state: RootState) => state.funnelStatus.funnelStatusMap;

export const selectFunnelStatus = (funnelId: string) => (state: RootState) =>
  state.funnelStatus.funnelStatusMap[funnelId];

export const selectFunnelStatuses = (funnelIds: string[]) => (state: RootState) =>
  funnelIds.reduce(
    (acc, funnelId) => {
      acc[funnelId] = state.funnelStatus.funnelStatusMap[funnelId];
      return acc;
    },
    {} as { [funnelId: string]: FunnelStatus | undefined },
  );

export default funnelStatusSlice.reducer;
